import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Injector,
  Input,
  OnDestroy,
} from '@angular/core';
import { TooltipPosition } from '../enums';
import { ReusableTooltipComponent } from '../tooltip/reusable-tooltip/reusable-tooltip.component';

@Directive({
  selector: '[appReusableTooltip]',
})
export class ReusableTooltipDirective implements OnDestroy {
  @Input() tooltip = '';
  @Input() width = '';
  @Input() position: TooltipPosition = TooltipPosition.DEFAULT;
  private readonly showDelay = 300;
  private readonly hideDelay = 0;
  private componentRef: ComponentRef<any> | null = null;
  private showTimeout?: number;

  constructor(
    private elementRef: ElementRef,
    private appRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.initializeTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    window.setTimeout(this.destroy.bind(this), this.hideDelay);
  }

  private initializeTooltip() {
    if (this.componentRef === null) {
      window.clearInterval(this.hideDelay);
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ReusableTooltipComponent);
      this.componentRef = componentFactory.create(this.injector);

      this.appRef.attachView(this.componentRef.hostView);
      const [tooltipDOMElement] = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes;

      this.setTooltipComponentProperties();

      document.body.appendChild(tooltipDOMElement);
      this.showTimeout = window.setTimeout(this.showTooltip.bind(this), this.showDelay);
    }
  }

  private showTooltip() {
    if (this.componentRef !== null) {
      this.componentRef.instance.visible = true;
    }
  }

  private setTooltipComponentProperties() {
    if (this.componentRef !== null) {
      this.componentRef.instance.text = this.tooltip;
      this.componentRef.instance.position = this.position;
      if (this.width) {
        this.componentRef.instance.width = this.width + 'px';
      }

      const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();

      switch (this.position) {
        case TooltipPosition.BELOW: {
          this.componentRef.instance.left = Math.round((right - left) / 2 + left) + 'px';
          this.componentRef.instance.top = Math.round(bottom) + 'px';
          break;
        }
        case TooltipPosition.ABOVE: {
          this.componentRef.instance.left = Math.round((right - left) / 2 + left) + 'px';
          this.componentRef.instance.top = Math.round(top) + 'px';
          break;
        }
        case TooltipPosition.RIGHT: {
          this.componentRef.instance.left = Math.round(right) + 'px';
          this.componentRef.instance.top = Math.round(top + (bottom - top) / 2) + 'px';
          break;
        }
        case TooltipPosition.LEFT: {
          this.componentRef.instance.left = Math.round(left) + 'px';
          this.componentRef.instance.top = Math.round(top + (bottom - top) / 2) + 'px';
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  private destroy(): void {
    if (this.componentRef !== null) {
      window.clearInterval(this.showTimeout);
      window.clearInterval(this.hideDelay);
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
